/* Level Access manual evaluation fixes (Oct 2026).
 * Runtime patches for markup the theme can't change at the source (app widgets, dynamically injected content).
 * Every patch is idempotent and re-runs (debounced) when the DOM changes.
 */
(() => {
  'use strict';

  const patches = [];
  const add = (name, fn) => patches.push({ name, fn });

  /** Polite screen reader announcement through one shared live region. */
  let liveEl;
  const announce = (message) => {
    if (!liveEl) {
      liveEl = document.createElement('div');
      liveEl.className = 'visually-hidden';
      liveEl.setAttribute('role', 'status');
      liveEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveEl);
    }
    liveEl.textContent = '';
    setTimeout(() => { liveEl.textContent = message; }, 80);
  };
  window.loomA11yAnnounce = announce;

  let timer;
  const schedule = () => { clearTimeout(timer); timer = setTimeout(run, 120); };
  const run = () => {
    for (const p of patches) {
      try { p.fn(); } catch (error) { console.warn('[a11y-fixes] ' + p.name, error); }
    }
  };

  // ---- patches are registered below ----

  // Slideshow Play/Pause (E398): only one button is shown at a time, so hand focus to the other one after a toggle.
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('.slideshow-control.icon-pause, .slideshow-control.icon-play') : null;
    if (!button) return;
    const isPause = button.classList.contains('icon-pause');
    requestAnimationFrame(() => {
      if (button.offsetParent !== null) return;
      const other = button.parentElement?.querySelector(isPause ? '.slideshow-control.icon-play' : '.slideshow-control.icon-pause');
      if (other instanceof HTMLElement) other.focus();
    });
  });

  // Hero (E717 reflow at 320px, E719 text spacing): when the slide copy would run up under the overlaid header,
  // start it below the header and let the slides grow instead of clipping.
  const clearHeader = () => {
    const header = document.querySelector('header-component') || document.querySelector('#header-group .header');
    document.querySelectorAll('.slideshow-banner slideshow-slides').forEach((slides) => {
      const contents = [...slides.querySelectorAll('.slide__content')];
      slides.classList.remove('a11y-hero-grow');
      contents.forEach((content) => content.classList.remove('a11y-clear-header'));
      if (!header) return;
      const headerBottom = header.getBoundingClientRect().bottom;
      let needed = 0;
      contents.forEach((content) => {
        const first = content.firstElementChild && content.firstElementChild.firstElementChild;
        if (!first) return;
        const top = content.getBoundingClientRect().top;
        const clearance = headerBottom - top + 16;
        if (first.getBoundingClientRect().top - top < clearance) needed = Math.max(needed, clearance);
      });
      if (!needed) return;
      slides.style.setProperty('--a11y-hero-min-height', Math.round(slides.getBoundingClientRect().height) + 'px');
      slides.style.setProperty('--a11y-hero-clearance', Math.round(needed) + 'px');
      slides.classList.add('a11y-hero-grow');
      contents.forEach((content) => content.classList.add('a11y-clear-header'));
    });
  };
  let heroTimer;
  const scheduleHero = () => { clearTimeout(heroTimer); heroTimer = setTimeout(clearHeader, 200); };
  window.addEventListener('resize', scheduleHero);
  window.addEventListener('load', scheduleHero);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleHero);

  // ---- helpers for the patches below ----
  const once = (el, key) => { const k = 'a11y' + key; if (el.dataset[k]) return false; el.dataset[k] = '1'; return true; };
  let uidCount = 0;
  const uid = (prefix) => prefix + '-' + (++uidCount);
  /** Announce inside an open modal's own live region when there is one (aria-modal hides outside regions). */
  const announceNear = (el, message) => {
    const region = el && el.closest && el.closest('[aria-modal="true"]')?.querySelector('[role="status"]');
    if (!region) { announce(message); return; }
    region.textContent = '';
    setTimeout(() => { region.textContent = message; }, 80);
  };

  // Quantity +/- (E680): announce the new quantity
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('.quantity-plus, .quantity-minus, button[name="plus"], button[name="minus"]') : null;
    if (!button) return;
    const wrapper = button.closest('quantity-selector-component, .quantity-selector, .quantity, .cart-quantity');
    const input = wrapper && wrapper.querySelector('input[type="number"], input[name="quantity"], input[name="updates[]"], input');
    if (!input) return;
    setTimeout(() => announceNear(button, 'Quantity ' + input.value), 200);
  });

  // ===== MageWorx product options (E254, E257, E314) =====
  add('mw-options', () => {
    const options = [...document.querySelectorAll('.mw-option')];
    if (!options.length) return;
    if (options.some((o) => o.classList.contains('mw-option--required')) && !document.querySelector('.a11y-mw-required-note')) {
      const note = document.createElement('p');
      note.className = 'a11y-mw-required-note';
      note.textContent = 'Fields marked with an asterisk (*) are required.';
      options[0].parentElement.insertBefore(note, options[0]);
    }
    options.forEach((option) => {
      const titleEl = option.querySelector('.mw-label .mw-text--style-strong');
      const title = (titleEl ? titleEl.textContent : '').trim();
      if (option.classList.contains('mw-option--type-radio')) {
        const group = option.querySelector('.mw-option__container') || option;
        if (titleEl && !titleEl.id) titleEl.id = uid('mw-option-title');
        if (group.getAttribute('role') !== 'radiogroup') {
          group.setAttribute('role', 'radiogroup');
          if (titleEl) group.setAttribute('aria-labelledby', titleEl.id);
        }
        if (option.classList.contains('mw-option--required')) group.setAttribute('aria-required', 'true');
      }
      option.querySelectorAll('.mw-tooltip--icon').forEach((icon) => {
        if (!once(icon, 'Tip')) return;
        icon.setAttribute('tabindex', '0');
        icon.setAttribute('role', 'button');
        icon.setAttribute('aria-label', 'More information about ' + (title || 'this option'));
        const tip = () => icon._tippy;
        icon.addEventListener('focus', () => tip() && tip().show());
        icon.addEventListener('blur', () => tip() && tip().hide());
        icon.addEventListener('keydown', (event) => {
          const t = tip();
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (t) { if (t.state && t.state.isShown) t.hide(); else t.show(); }
          } else if (event.key === 'Escape' && t) {
            t.hide();
          }
        });
      });
    });
  });

  // ===== Judge.me reviews =====
  let jdgmUploading = false;
  add('judgeme', () => {
    // Preview badge: descriptive name and focus moves to the reviews (E411, E399)
    document.querySelectorAll('.jdgm-prev-badge__stars[role="button"]').forEach((stars) => {
      const badge = stars.closest('.jdgm-prev-badge');
      const score = parseFloat(stars.getAttribute('data-score') || '0');
      const count = parseInt(((badge && badge.querySelector('.jdgm-prev-badge__text')) || {}).textContent || '', 10);
      const label = (count > 0 ? 'Rated ' + score + ' out of 5 stars based on ' + count + ' review' + (count === 1 ? '' : 's') + '. ' : '') + 'See all reviews';
      if (stars.getAttribute('aria-label') !== label) stars.setAttribute('aria-label', label);
      if (once(stars, 'Badge')) {
        const moveFocus = () => setTimeout(() => {
          const target = document.querySelector('.jm-review-widget-minimal-header__title, .jdgm-rev-widg__title, .jdgm-review-widget');
          if (target instanceof HTMLElement) {
            if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
            target.focus();
          }
        }, 500);
        stars.addEventListener('click', moveFocus);
        stars.addEventListener('keydown', (event) => { if (event.key === 'Enter' || event.key === ' ') moveFocus(); });
      }
    });
    // Widget title "Customer Reviews" is a heading (E252)
    document.querySelectorAll('.jm-review-widget-minimal-header__title, .jdgm-rev-widg__title').forEach((title) => {
      if (!title.matches('h1, h2, h3, h4, h5, h6') && title.getAttribute('role') !== 'heading') {
        title.setAttribute('role', 'heading');
        title.setAttribute('aria-level', '2');
      }
    });
    // Popup review card link without a name
    document.querySelectorAll('.jdgm-popup-card__full-rev-link').forEach((link) => {
      if (!link.getAttribute('aria-label') && !link.textContent.trim()) link.setAttribute('aria-label', 'Read full review');
    });
    // Write-a-review modal
    document.querySelectorAll('.jdgm-review-widget-modal').forEach((modal) => {
      modal.querySelectorAll('.jdgm-write-review-modal__title[role="heading"]').forEach((h) => h.setAttribute('aria-level', '2')); // E576, E895
      modal.querySelectorAll('.jdgm-write-review-modal__product-title[role="heading"]').forEach((h) => { h.removeAttribute('role'); h.removeAttribute('aria-level'); }); // E619
      modal.querySelectorAll('.jdgm-write-review-modal__product-image img').forEach((img) => { if (img.getAttribute('alt') !== '') img.setAttribute('alt', ''); }); // E630
      modal.querySelectorAll('.jdgm-write-review-modal__nav-btn').forEach((button) => { // E620, E343: name matches the visible label
        const text = button.textContent.trim();
        if (text && button.getAttribute('aria-label') !== text) button.setAttribute('aria-label', text);
      });
      const productTitle = ((modal.querySelector('.jdgm-write-review-modal__product-title') || {}).textContent || '').trim();
      const sections = modal.querySelector('.jdgm-write-review-modal__sections[role="region"]');
      if (sections && productTitle) sections.setAttribute('aria-label', 'Write a review for ' + productTitle); // E666
      const mediaInput = modal.querySelector('.jdgm-write-review-modal__media-input');
      if (mediaInput) mediaInput.setAttribute('aria-label', 'Click to upload or drag and drop a picture or video'); // E345
      const starsGroup = modal.querySelector('.jdgm-write-review-modal__stars');
      if (starsGroup) { // E629: which rating is selected; E896: "Poor" / "Great" hints
        const stars = [...starsGroup.querySelectorAll('button.jdgm-star')];
        const score = parseInt(((starsGroup.querySelector('input[name="score"]') || {}).value) || '0', 10);
        stars.forEach((star, i) => star.setAttribute('aria-pressed', String(score > 0 && i + 1 === score)));
        const hint = (word) => [...modal.querySelectorAll('span, div, p')].find((el) => !el.children.length && el.textContent.trim() === word);
        [['Poor', stars[0]], ['Great', stars[stars.length - 1]]].forEach(([word, star]) => {
          const el = hint(word);
          if (!el || !star) return;
          if (!el.id) el.id = uid('jdgm-hint');
          star.setAttribute('aria-describedby', el.id);
        });
      }
      modal.querySelectorAll('.jdgm-write-review-modal__media-delete').forEach((button, i) => button.setAttribute('aria-label', 'Remove uploaded file ' + (i + 1))); // E554
      const uploading = Boolean(modal.querySelector('.jdgm-is-uploading')); // E346
      if (uploading !== jdgmUploading) {
        jdgmUploading = uploading;
        announceNear(modal.querySelector('[role="status"]') || modal, uploading ? 'Uploading file' : 'File uploaded');
      }
    });
  });
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('.jdgm-write-review-modal__stars button.jdgm-star')) setTimeout(schedule, 60);
    if (target.closest('.jdgm-write-review-modal__media-delete')) setTimeout(() => announceNear(target, 'File removed'), 150); // E349
    if (target.closest('.jm-filter-panel__clear-btn')) setTimeout(() => announceNear(target, 'Filters cleared'), 300); // E485
    const nav = target.closest('.jm-navigation__previous, .jm-navigation__next'); // E681
    if (nav) {
      setTimeout(() => {
        const scope = nav.closest('[role="dialog"]') || document;
        const counter = [...scope.querySelectorAll('span, div, p')].find((el) => !el.children.length && /^\s*\d+\s*(\/|of)\s*\d+\s*$/.test(el.textContent));
        announceNear(nav, counter ? 'Image ' + counter.textContent.trim().replace(/\s*\/\s*/, ' of ') : (nav.classList.contains('jm-navigation__next') ? 'Next image shown' : 'Previous image shown'));
      }, 350);
    }
  });

  // ---- boot ----
  const boot = () => {
    run();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
